
import { Component, Input, OnInit, inject } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NgControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { InputAddLeadingZeroDirective } from '../../directives/input/add-leading-zero/add-leading-zero';
import { InputNumbersOnlyDirective } from '../../directives/input/numbers-only/numbers-only';
import { CerrixDatepickerComponent } from '../cerrix-date-picker/cerrix-datepicker.component';
import { CerrixDatePipe } from '../pipes/cerrix-date.pipe';
import { ErrorMessagesPipe } from '../pipes/error-messages.pipe';
import { CerrixValidators } from '../validation/CerrixValidators';
import { DefaultErrorStateMatcher } from '../validation/DefaultErrorStateMatcher';

@Component({
    selector: 'cerrix-datetime-picker',
    imports: [
    ErrorMessagesPipe,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    CerrixDatepickerComponent,
    CerrixDatePipe,
    InputNumbersOnlyDirective,
    InputAddLeadingZeroDirective
],
    templateUrl: './cerrix-datetime-picker.component.html',
    styleUrl: './cerrix-datetime-picker.component.scss',
    providers: []
})
export class CerrixDatetimePickerComponent
  implements OnInit, ControlValueAccessor
{
  private static readonly maxHours: number = 23;
  private static readonly maxMinutes: number = 59;
  private static readonly minTime: number = 0;

  protected ngControl = inject(NgControl, { optional: true, self: true });

  @Input() public value: Date | undefined = undefined;
  @Input() public placeholder: string = '';
  @Input() public disabled: boolean = this.ngControl?.disabled ?? false;
  @Input() public minDate: Date | undefined = undefined;
  @Input() public maxDate: Date | undefined = undefined;
   @Input() public dataUiid: string = "undefined"

  protected maxHours: number =
    this.maxDate?.getHours() ?? CerrixDatetimePickerComponent.maxHours;
  protected minHours: number =
    this.minDate?.getHours() ?? CerrixDatetimePickerComponent.minTime;
  protected maxMinutes: number =
    this.maxDate?.getMinutes() ?? CerrixDatetimePickerComponent.maxMinutes;
  protected minMinutes: number =
    this.minDate?.getMinutes() ?? CerrixDatetimePickerComponent.minTime;

  protected dateFormControl!: FormControl<Date | null | undefined>;
  protected hoursFormControl!: FormControl<string | null | undefined>;
  protected minutesFormControl!: FormControl<string | null | undefined>;

  protected errorMatcher!: ErrorStateMatcher;

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
      this.errorMatcher = new DefaultErrorStateMatcher();
    }
  }

  ngOnInit(): void {
    let date: Date | undefined = undefined;
    let hours: string | undefined = undefined;
    let minutes: string | undefined = undefined;

    if (this.value != undefined) {
      const parsedDate = new Date(this.value);
      date = parsedDate;
      hours = parsedDate.getHours().toString();
      minutes = parsedDate.getMinutes().toString();
    }

    const isRequired = this.ngControl?.control?.hasValidator(
      Validators.required
    );

    this.dateFormControl = new FormControl(
      { value: date, disabled: this.disabled },
      isRequired ? [CerrixValidators.required('Date is required')] : []
    );

    const hoursValidator = [
      CerrixValidators.min(
        this.minHours,
        `Hours must be between ${this.minHours} and ${this.maxHours}`
      ),
      CerrixValidators.max(
        this.maxHours,
        `Hours must be between ${this.minHours} and ${this.maxHours}`
      ),
    ];
    this.hoursFormControl = new FormControl(
      { value: hours, disabled: this.disabled },
      isRequired
        ? [
            CerrixValidators.required('Please provide time in hours'),
            ...hoursValidator,
          ]
        : hoursValidator
    );

    const minutesValidator = [
      CerrixValidators.min(
        this.minMinutes,
        `Minutes must be between ${this.minMinutes} and ${this.maxMinutes}`
      ),
      CerrixValidators.max(
        this.maxMinutes,
        `Minutes must be between ${this.minMinutes} and ${this.maxMinutes}`
      ),
    ];
    this.minutesFormControl = new FormControl(
      { value: minutes, disabled: this.disabled },
      isRequired
        ? [
            CerrixValidators.required('Please provide time in minutes'),
            ...minutesValidator,
          ]
        : minutesValidator
    );
  }

  private onValueChange = (value: Date | undefined) => {
    this.onTouched();
  };

  private onTouched = () => {
    this.ngControl?.control?.markAsTouched();
  };

  public writeValue(value: Date): void {
    this.value = value;
  }
  public registerOnChange(onChange: (value: Date | undefined) => void): void {
    this.onValueChange = onChange;
  }
  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (isDisabled) {
      this.dateFormControl?.disable();
      this.hoursFormControl?.disable();
      this.minutesFormControl?.disable();
    } else {
      this.dateFormControl?.enable();
      this.hoursFormControl?.enable();
      this.minutesFormControl?.enable();
    }
  }

  protected onDateTimeChange(): void {
    const date = this.dateFormControl.value;
    const hours = this.hoursFormControl.value;
    const minutes = this.minutesFormControl.value;

    const parsedHours = parseInt(hours ?? '');
    const parsedMinutes = parseInt(minutes ?? '');
    if (
      date != undefined &&
      hours != undefined &&
      hours !== '' &&
      parsedHours >= this.minHours &&
      parsedHours <= this.maxHours &&
      minutes != undefined &&
      minutes !== '' &&
      parsedMinutes >= this.minMinutes &&
      parsedMinutes <= this.maxMinutes
    ) {
      const parsedDate = new Date(date);
      this.value = new Date(
        parsedDate.getFullYear(),
        parsedDate.getMonth(),
        parsedDate.getDate(),
        parsedHours,
        parsedMinutes
      );
    } else {
      this.value = undefined;
    }

    this.onValueChange(this.value);

    this.ngControl?.control?.markAsDirty();
    this.ngControl?.control?.markAsTouched();
  }
}
